import React, { ComponentType, ReactElement } from 'react';
import { LoadingBoundary, LoadingState } from './LoadingBoundary';
import { useVRPUI } from '../../contexts/VRPUIContext';
import { useDomainTheme } from '../../contexts/DomainThemeContext';

export interface ContextState {
  /** Loading state */
  loading: boolean;
  /** Error state */
  error: Error | string | null;
  /** Data loaded state */
  loaded: boolean;
  /** Retry function */
  retry?: () => void;
}

export interface ContextLoadingOptions {
  /** Loading message */
  loadingMessage?: string;
  /** Error message prefix */
  errorMessage?: string;
  /** Skeleton variant */
  skeletonVariant?: 'text' | 'card' | 'table' | 'dashboard' | 'list';
  /** Number of skeleton items */
  skeletonCount?: number;
  /** Minimum loading time */
  minLoadTime?: number;
  /** Full height container */
  fullHeight?: boolean;
  /** Required contexts to wait for */
  requiredContexts?: string[];
  /** Custom context validator */
  contextValidator?: () => boolean;
  /** Show retry on error */
  showRetry?: boolean;
}

/**
 * Hook to check context loading states
 */
export const useContextState = (options: ContextLoadingOptions = {}): ContextState => {
  const [internalLoading, setInternalLoading] = React.useState(true);
  const [error, setError] = React.useState<Error | string | null>(null);
  const [retryCount, setRetryCount] = React.useState(0);

  // Get context states
  const vrpUIContext = React.useContext(React.createContext(null));
  const domainContext = React.useContext(React.createContext(null));

  const checkContexts = React.useCallback(() => {
    try {
      setError(null);
      
      // Check if required contexts are available
      const { requiredContexts = [], contextValidator } = options;
      
      if (requiredContexts.includes('vrpui')) {
        if (!vrpUIContext) {
          throw new Error('VRP UI Context not available');
        }
      }
      
      if (requiredContexts.includes('domain')) {
        if (!domainContext) {
          throw new Error('Domain Theme Context not available');
        }
      }
      
      // Custom validation
      if (contextValidator && !contextValidator()) {
        throw new Error('Context validation failed');
      }
      
      setInternalLoading(false);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err : String(err));
      setInternalLoading(false);
      return false;
    }
  }, [vrpUIContext, domainContext, options, retryCount]);

  React.useEffect(() => {
    const timer = setTimeout(checkContexts, 100);
    return () => clearTimeout(timer);
  }, [checkContexts]);

  const retry = React.useCallback(() => {
    setRetryCount(prev => prev + 1);
    setInternalLoading(true);
    setError(null);
  }, []);

  return {
    loading: internalLoading,
    error,
    loaded: !internalLoading && !error,
    retry,
  };
};

/**
 * Higher-order component that provides context loading boundaries
 */
export function withContextLoading<P extends object>(
  Component: ComponentType<P>,
  options: ContextLoadingOptions = {}
) {
  const WrappedComponent = React.forwardRef<any, P>((props, ref) => {
    const contextState = useContextState(options);
    
    const getLoadingState = (): LoadingState => {
      if (contextState.loading) return 'loading';
      if (contextState.error) return 'error';
      if (contextState.loaded) return 'success';
      return 'idle';
    };

    const getErrorMessage = (): string => {
      if (!contextState.error) return '';
      
      const baseMessage = options.errorMessage || 'Failed to initialize';
      const errorDetails = typeof contextState.error === 'string' 
        ? contextState.error 
        : contextState.error.message;
      
      return `${baseMessage}: ${errorDetails}`;
    };

    return (
      <LoadingBoundary
        state={getLoadingState()}
        loadingMessage={options.loadingMessage || 'Initializing application...'}
        errorMessage={getErrorMessage()}
        skeletonVariant={options.skeletonVariant || 'dashboard'}
        skeletonCount={options.skeletonCount || 3}
        minLoadTime={options.minLoadTime || 300}
        fullHeight={options.fullHeight}
        showRetry={options.showRetry !== false}
        onRetry={contextState.retry}
      >
        <Component {...props} ref={ref} />
      </LoadingBoundary>
    );
  });

  WrappedComponent.displayName = `withContextLoading(${Component.displayName || Component.name})`;
  
  return WrappedComponent;
}

/**
 * Specialized HOC for VRP UI context dependencies
 */
export function withVRPUILoading<P extends object>(
  Component: ComponentType<P>,
  options: Omit<ContextLoadingOptions, 'requiredContexts'> = {}
) {
  return withContextLoading(Component, {
    ...options,
    requiredContexts: ['vrpui'],
    loadingMessage: options.loadingMessage || 'Loading VRP interface...',
    contextValidator: () => {
      try {
        // This will throw if context is not available
        useVRPUI();
        return true;
      } catch {
        return false;
      }
    },
  });
}

/**
 * Specialized HOC for Domain Theme context dependencies
 */
export function withDomainThemeLoading<P extends object>(
  Component: ComponentType<P>,
  options: Omit<ContextLoadingOptions, 'requiredContexts'> = {}
) {
  return withContextLoading(Component, {
    ...options,
    requiredContexts: ['domain'],
    loadingMessage: options.loadingMessage || 'Loading theme configuration...',
    contextValidator: () => {
      try {
        // This will throw if context is not available
        useDomainTheme();
        return true;
      } catch {
        return false;
      }
    },
  });
}

/**
 * Combined HOC for components that need both VRP UI and Domain Theme contexts
 */
export function withFullContextLoading<P extends object>(
  Component: ComponentType<P>,
  options: Omit<ContextLoadingOptions, 'requiredContexts'> = {}
) {
  return withContextLoading(Component, {
    ...options,
    requiredContexts: ['vrpui', 'domain'],
    loadingMessage: options.loadingMessage || 'Initializing application...',
    contextValidator: () => {
      try {
        useVRPUI();
        useDomainTheme();
        return true;
      } catch {
        return false;
      }
    },
  });
}

/**
 * Hook for progressive context loading with multiple stages
 */
export interface ProgressiveContextState {
  stage: 'initializing' | 'loading-ui' | 'loading-theme' | 'ready' | 'error';
  progress: number;
  message: string;
  error: Error | null;
  retry: () => void;
}

export const useProgressiveContextLoading = (): ProgressiveContextState => {
  const [stage, setStage] = React.useState<ProgressiveContextState['stage']>('initializing');
  const [progress, setProgress] = React.useState(0);
  const [message, setMessage] = React.useState('Initializing...');
  const [error, setError] = React.useState<Error | null>(null);
  const [retryCount, setRetryCount] = React.useState(0);

  const loadContexts = React.useCallback(async () => {
    try {
      setError(null);
      setStage('initializing');
      setProgress(0);
      setMessage('Starting application...');

      // Stage 1: Load UI Context
      setStage('loading-ui');
      setProgress(25);
      setMessage('Loading interface configuration...');
      
      await new Promise(resolve => setTimeout(resolve, 200)); // Simulate loading
      
      try {
        useVRPUI();
      } catch (err) {
        throw new Error('Failed to load UI context');
      }

      // Stage 2: Load Theme Context
      setStage('loading-theme');
      setProgress(60);
      setMessage('Loading theme configuration...');
      
      await new Promise(resolve => setTimeout(resolve, 200));
      
      try {
        useDomainTheme();
      } catch (err) {
        throw new Error('Failed to load theme context');
      }

      // Stage 3: Ready
      setProgress(100);
      setMessage('Application ready');
      setStage('ready');

    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      setStage('error');
      setMessage('Failed to initialize application');
    }
  }, [retryCount]);

  React.useEffect(() => {
    loadContexts();
  }, [loadContexts]);

  const retry = React.useCallback(() => {
    setRetryCount(prev => prev + 1);
  }, []);

  return {
    stage,
    progress,
    message,
    error,
    retry,
  };
};

/**
 * Context loading boundary component for manual use
 */
export interface ContextLoadingBoundaryProps {
  children: React.ReactNode;
  options?: ContextLoadingOptions;
}

export const ContextLoadingBoundary: React.FC<ContextLoadingBoundaryProps> = ({
  children,
  options = {},
}) => {
  const contextState = useContextState(options);
  
  const getLoadingState = (): LoadingState => {
    if (contextState.loading) return 'loading';
    if (contextState.error) return 'error';
    if (contextState.loaded) return 'success';
    return 'idle';
  };

  return (
    <LoadingBoundary
      state={getLoadingState()}
      loadingMessage={options.loadingMessage || 'Loading...'}
      errorMessage={contextState.error ? String(contextState.error) : undefined}
      skeletonVariant={options.skeletonVariant}
      skeletonCount={options.skeletonCount}
      minLoadTime={options.minLoadTime}
      fullHeight={options.fullHeight}
      showRetry={options.showRetry !== false}
      onRetry={contextState.retry}
    >
      {children}
    </LoadingBoundary>
  );
};

export default withContextLoading;